import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, User, Bot, MessageSquare, Send, Headphones, X, MessageSquareText } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import SupportAttachment from "@/components/SupportAttachment";
import AttachmentPreview from "@/components/AttachmentPreview";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ChatMessage {
  id: string;
  user_id: string;
  message: string;
  role: string;
  created_at: string;
  admin_id: string | null;
  admin_name?: string | null;
  admin_email?: string | null;
  attachment_url?: string | null;
  attachment_type?: string | null;
}

interface SupportRequest {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface UserProfile {
  full_name: string | null;
  email: string;
}

interface GroupedChat {
  user_id: string;
  messages: ChatMessage[];
  lastMessageAt: string;
  supportRequest: SupportRequest | null;
  userProfile?: UserProfile | null;
}

const AdminSupportChat = () => {
  const { user: adminUser } = useAuth();
  const { toast } = useToast();
  const [groupedChats, setGroupedChats] = useState<GroupedChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState<{ url: string; type: string } | null>(null);
  const [quickReplies, setQuickReplies] = useState<{ id: string; title: string; content: string; category: string | null }[]>([]);
  const [quickReplyOpen, setQuickReplyOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    try {
      // Fetch all messages
      const { data: messagesData, error: messagesError } = await supabase
        .from("chat_messages")
        .select("*, attachment_url, attachment_type")
        .order("created_at", { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch all support requests
      const { data: requestsData, error: requestsError } = await supabase
        .from("support_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (requestsError) throw requestsError;

      // Fetch admin profiles for messages with admin_id
      const adminIds = [...new Set((messagesData || []).filter(m => m.admin_id).map(m => m.admin_id))];
      let adminProfiles: Record<string, { full_name: string | null; email: string }> = {};
      
      if (adminIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", adminIds);
        
        if (profilesData) {
          adminProfiles = profilesData.reduce((acc, p) => {
            acc[p.user_id] = { full_name: p.full_name, email: p.email };
            return acc;
          }, {} as Record<string, { full_name: string | null; email: string }>);
        }
      }

      // Fetch user profiles for all users in chats
      const userIds = [...new Set((messagesData || []).map(m => m.user_id))];
      let userProfiles: Record<string, UserProfile> = {};
      
      if (userIds.length > 0) {
        const { data: userProfilesData } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", userIds);
        
        if (userProfilesData) {
          userProfiles = userProfilesData.reduce((acc, p) => {
            acc[p.user_id] = { full_name: p.full_name, email: p.email };
            return acc;
          }, {} as Record<string, UserProfile>);
        }
      }

      // Enrich messages with admin info
      const enrichedMessages = (messagesData || []).map(msg => ({
        ...msg,
        admin_name: msg.admin_id ? adminProfiles[msg.admin_id]?.full_name : null,
        admin_email: msg.admin_id ? adminProfiles[msg.admin_id]?.email : null,
      }));

      // Group messages by user
      const grouped: Record<string, ChatMessage[]> = {};
      enrichedMessages.forEach((msg) => {
        if (!grouped[msg.user_id]) {
          grouped[msg.user_id] = [];
        }
        grouped[msg.user_id].push(msg);
      });

      // Create grouped chats with support request info
      const groupedArray: GroupedChat[] = Object.entries(grouped).map(([user_id, messages]) => {
        const userRequest = (requestsData || []).find(
          (r) => r.user_id === user_id && r.status !== "closed"
        );
        return {
          user_id,
          messages,
          lastMessageAt: messages[messages.length - 1]?.created_at || "",
          supportRequest: userRequest || null,
          userProfile: userProfiles[user_id] || null,
        };
      });

      // Sort by support request (pending first) then by last message
      groupedArray.sort((a, b) => {
        // Pending support requests first
        if (a.supportRequest?.status === "pending" && b.supportRequest?.status !== "pending") return -1;
        if (b.supportRequest?.status === "pending" && a.supportRequest?.status !== "pending") return 1;
        // Then active support requests
        if (a.supportRequest && !b.supportRequest) return -1;
        if (b.supportRequest && !a.supportRequest) return 1;
        // Then by date
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      });

      setGroupedChats(groupedArray);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Fetch quick replies
    const fetchQuickReplies = async () => {
      const { data } = await supabase
        .from("quick_replies")
        .select("id, title, content, category")
        .eq("is_active", true)
        .order("category");
      if (data) setQuickReplies(data);
    };
    fetchQuickReplies();

    // Set up realtime subscription
    const channel = supabase
      .channel('admin-support-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_messages' },
        () => fetchData()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_requests' },
        () => fetchData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedUserId, groupedChats]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return "الآن";
    if (diffMinutes < 60) return `منذ ${diffMinutes} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    return `منذ ${diffDays} يوم`;
  };

  const handleSendReply = async () => {
    if (!selectedUserId || (!replyMessage.trim() && !attachment) || !adminUser) return;

    setSending(true);
    try {
      // Check if this is the first reply to a pending support request
      const currentChat = groupedChats.find(c => c.user_id === selectedUserId);
      const isFirstReply = currentChat?.supportRequest?.status === "pending";

      // If first reply, auto-send greeting message first
      if (isFirstReply) {
        const greetingMessage = "مرحبا انا منذر كيف يمكنني مساعدتك";
        await supabase.from("chat_messages").insert({
          user_id: selectedUserId,
          message: greetingMessage,
          role: "assistant",
          admin_id: adminUser.id,
          is_support_request: true,
          attachment_url: null,
          attachment_type: null,
        });
      }

      const { error } = await supabase.from("chat_messages").insert({
        user_id: selectedUserId,
        message: replyMessage,
        role: "assistant",
        admin_id: adminUser.id,
        is_support_request: true,
        attachment_url: attachment?.url || null,
        attachment_type: attachment?.type || null,
      });

      if (error) throw error;

      // Update support request status to active
      if (isFirstReply && currentChat?.supportRequest) {
        await supabase
          .from("support_requests")
          .update({ status: "active", updated_at: new Date().toISOString() })
          .eq("id", currentChat.supportRequest.id);
      }

      setReplyMessage("");
      setAttachment(null);
      toast({
        title: "تم الإرسال",
        description: "تم إرسال الرد بنجاح",
      });
    } catch (error) {
      console.error("Error sending reply:", error);
      toast({
        title: "خطأ",
        description: "فشل في إرسال الرد",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentUpload = (url: string, type: string) => {
    if (url) {
      setAttachment({ url, type });
    } else {
      setAttachment(null);
    }
  };

  const handleCloseSupport = async (requestId: string) => {
    try {
      await supabase
        .from("support_requests")
        .update({ status: "closed", closed_at: new Date().toISOString() })
        .eq("id", requestId);

      toast({
        title: "تم الإغلاق",
        description: "تم إغلاق طلب الدعم",
      });
    } catch (error) {
      console.error("Error closing support:", error);
    }
  };

  const selectedChat = groupedChats.find((g) => g.user_id === selectedUserId);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Users List */}
      <Card className="glass border-border lg:col-span-1">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            المحادثات ({groupedChats.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {groupedChats.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground px-4">
              لا توجد رسائل دعم حتى الآن
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-border">
                {groupedChats.map((chat) => {
                  const userMessagesCount = chat.messages.filter((m) => m.role === "user").length;
                  const hasPendingSupport = chat.supportRequest?.status === "pending";
                  const hasActiveSupport = chat.supportRequest?.status === "active";

                  return (
                    <button
                      key={chat.user_id}
                      onClick={() => setSelectedUserId(chat.user_id)}
                      className={`w-full p-4 text-right hover:bg-secondary/50 transition-colors ${
                        selectedUserId === chat.user_id ? "bg-secondary" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {hasPendingSupport && (
                            <Badge variant="destructive" className="animate-pulse">
                              <Headphones className="w-3 h-3 ml-1" />
                              طلب دعم
                            </Badge>
                          )}
                          {hasActiveSupport && (
                            <Badge variant="default" className="bg-green-600">
                              <Headphones className="w-3 h-3 ml-1" />
                              نشط
                            </Badge>
                          )}
                          <Badge variant="secondary">{userMessagesCount} رسالة</Badge>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      <div className="mt-2 text-right">
                        <p className="text-sm font-semibold text-foreground">
                          {chat.userProfile?.full_name || "مستخدم"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {chat.userProfile?.email || chat.user_id.slice(0, 8) + "..."}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-muted-foreground">
                          آخر رسالة: {formatDate(chat.lastMessageAt)}
                        </p>
                        {chat.supportRequest && chat.supportRequest.status !== "closed" && (
                          <p className="text-xs font-medium text-primary">
                            {getTimeAgo(chat.supportRequest.created_at)}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Messages View */}
      <Card className="glass border-border lg:col-span-2 flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-foreground">
              {selectedChat ? (selectedChat.userProfile?.full_name || "مستخدم") : "اختر محادثة لعرضها"}
            </CardTitle>
            {selectedChat?.userProfile?.email && (
              <p className="text-xs text-muted-foreground mt-1">{selectedChat.userProfile.email}</p>
            )}
          </div>
          {selectedChat?.supportRequest && selectedChat.supportRequest.status !== "closed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCloseSupport(selectedChat.supportRequest!.id)}
              className="text-destructive"
            >
              <X className="w-4 h-4 ml-2" />
              إغلاق الطلب
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          {!selectedUserId ? (
            <div className="text-center py-20 text-muted-foreground flex-1 flex flex-col items-center justify-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>اختر محادثة من القائمة لعرض الرسائل</p>
            </div>
          ) : selectedChat ? (
            <>
              <ScrollArea className="flex-1 h-[400px] pr-4" ref={scrollRef}>
                <div className="space-y-4">
                  {selectedChat.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.role !== "user" && (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.admin_id ? "bg-green-600/20" : "bg-primary/20"
                        }`}>
                          {msg.admin_id ? (
                            <Headphones className="w-4 h-4 text-green-600" />
                          ) : (
                            <Bot className="w-4 h-4 text-primary" />
                          )}
                        </div>
                      )}
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : msg.admin_id
                            ? "bg-green-600/20 text-foreground border border-green-600/30"
                            : "bg-secondary text-foreground"
                        }`}
                      >
                        {msg.admin_id && (
                          <div className="mb-1">
                            <p className="text-xs text-green-600 font-medium">
                              {msg.admin_name || "فريق الدعم"}
                            </p>
                            {msg.admin_email && (
                              <p className="text-xs text-muted-foreground">{msg.admin_email}</p>
                            )}
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        {msg.attachment_url && msg.attachment_type && (
                          <AttachmentPreview url={msg.attachment_url} type={msg.attachment_type} />
                        )}
                        <p
                          className={`text-xs mt-1 ${
                            msg.role === "user" ? "text-primary-foreground/70" : "text-muted-foreground"
                          }`}
                        >
                          {formatDate(msg.created_at)}
                        </p>
                      </div>
                      {msg.role === "user" && (
                        <div className="w-8 h-8 rounded-full bg-crypto-gold/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-crypto-gold" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Reply Input */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-border items-center">
                <SupportAttachment 
                  onUpload={handleAttachmentUpload}
                  disabled={sending}
                />
                <Popover open={quickReplyOpen} onOpenChange={setQuickReplyOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" disabled={quickReplies.length === 0} title="ردود جاهزة">
                      <MessageSquareText className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start" dir="rtl">
                    <div className="p-3 border-b border-border">
                      <p className="text-sm font-medium text-foreground">الردود الجاهزة</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {quickReplies.map((qr) => (
                        <button
                          key={qr.id}
                          className="w-full text-right px-3 py-2 hover:bg-secondary/50 transition-colors border-b border-border/50 last:border-0"
                          onClick={() => {
                            setReplyMessage(qr.content);
                            setQuickReplyOpen(false);
                          }}
                        >
                          <p className="text-sm font-medium text-foreground">{qr.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{qr.content}</p>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Input
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="اكتب ردك هنا..."
                  disabled={sending}
                  className="flex-1"
                  dir="rtl"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                <Button onClick={handleSendReply} disabled={sending || (!replyMessage.trim() && !attachment)}>
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSupportChat;
